import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MdAdd, MdRemove, MdClose, MdShoppingCart, MdArrowForward, MdLocalShipping } from 'react-icons/md';
import { useCartStore } from '../store/cartStore';
import { listSkus } from '../services/skusApi';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image66 from '../assets/image66.png';
import image67 from '../assets/image67.png';
import AddToCartButton from '../components/shared/AddToCartButton';
import PopularCategories from '../components/shared/PopularCategories';
import '../styles/CartPage.css';

interface SuggestedItem {
  id: number;
  name: string;
  price: number;
  image: string;
}

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, clearCart, getTotal, addItem } = useCartStore();
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [suggested, setSuggested] = useState<SuggestedItem[]>([]);

  // Cross-sell suggestions from the live catalog.
  useEffect(() => {
    let cancelled = false;
    listSkus({ limit: 5, in_stock_only: true })
      .then((page) => {
        if (cancelled) return;
        setSuggested(
          page.data.map((s) => ({
            id: s.id,
            name: s.product_name,
            price: s.list_price ?? s.unit_cost ?? 0,
            image: s.image_url || image67,
          })),
        );
      })
      .catch(() => {
        /* leave suggestions empty on failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subtotal = getTotal();
  const shipping = subtotal > 100 ? 0 : 9.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleRemove = (productId: number) => {
    setRemovingId(productId);
    setTimeout(() => {
      removeItem(productId);
      setRemovingId(null);
    }, 300);
  };

  const handleAddSuggested = (p: SuggestedItem) => {
    addItem({
      id: p.id, name: p.name, slug: String(p.id), price: p.price,
      description: '', shortDescription: '', images: [p.image],
      category: '', brand: '',
      rating: 0, reviewCount: 0, inStock: true, stockCount: 10,
      tags: [], features: [], sku: '',
    }, 1);
  };

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <Breadcrumb />
        <section className="cart-page__empty-section">
          <div className="cart-page__empty">
            <MdShoppingCart className="cart-page__empty-icon" />
            <h2 className="cart-page__empty-heading">Your cart is empty</h2>
            <p className="cart-page__empty-text">
              Looks like you haven't added anything to your cart yet.
            </p>
            <button className="cart-page__empty-btn" onClick={() => navigate('/products')}>
              Browse Products <MdArrowForward />
            </button>
          </div>
        </section>

        {/* Still show suggestions when empty */}
        <section className="cart-page__suggested">
          <div className="cart-page__suggested-inner">
            <h2 className="cart-page__section-title">You Might Like</h2>
            <div className="cart-page__suggested-grid">
              {suggested.map((p) => (
                <div key={p.id} className="cart-page__sug-card" onClick={() => navigate(`/products/${p.id}`)}>
                  <div className="cart-page__sug-img-wrap">
                    <img src={p.image} alt={p.name} className="cart-page__sug-img" />
                  </div>
                  <div className="cart-page__sug-info">
                    <span className="cart-page__sug-name">{p.name}</span>
                    <span className="cart-page__sug-stock">In stock</span>
                    <div className="cart-page__sug-row">
                      <span className="cart-page__sug-price">${p.price.toFixed(2)}</span>
                      <AddToCartButton onClick={(e) => { e.stopPropagation(); handleAddSuggested(p); }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <PopularCategories />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Breadcrumb />

      {/* ── Section 1: Cart Items + Summary ── */}
      <section className="cart-page__main">
        <div className="cart-page__main-inner">
          {/* Left: Items Card */}
          <div className="cart-page__items-card">
            <div className="cart-page__header">
              <h1 className="cart-page__title">Shopping Cart</h1>
              <span className="cart-page__count">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
            </div>

            <div className="cart-page__list">
              {items.map((item, index) => (
                <div
                  key={`${item.product.id}-${item.selectedColor}-${item.selectedSize}`}
                  className={`cart-page__item ${removingId === item.product.id ? 'cart-page__item--removing' : ''}`}
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <Link to={`/products/${item.product.slug}`} className="cart-page__item-img-link">
                    <img src={item.product.images[0]} alt={item.product.name} className="cart-page__item-img" />
                  </Link>

                  <div className="cart-page__item-info">
                    <Link to={`/products/${item.product.slug}`} className="cart-page__item-name">
                      {item.product.name}
                    </Link>
                    <span className="cart-page__item-category">{item.product.category}</span>
                  </div>

                  <div className="cart-page__item-qty">
                    <button className="cart-page__qty-btn" onClick={() => updateQuantity(item.product.id, item.quantity - 1)}>
                      <MdRemove />
                    </button>
                    <span className="cart-page__qty-value">{item.quantity}</span>
                    <button className="cart-page__qty-btn" onClick={() => updateQuantity(item.product.id, item.quantity + 1)}>
                      <MdAdd />
                    </button>
                  </div>

                  <div className="cart-page__item-pricing">
                    <span className="cart-page__item-unit">${item.product.price.toFixed(2)} each</span>
                    <span className="cart-page__item-total">${(item.product.price * item.quantity).toFixed(2)}</span>
                  </div>

                  <button className="cart-page__item-remove" onClick={() => handleRemove(item.product.id)}>
                    <MdClose />
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-page__actions">
              <button className="cart-page__clear-btn" onClick={clearCart}>Clear Cart</button>
              <Link to="/products" className="cart-page__continue-link">Continue Shopping</Link>
            </div>
          </div>

          {/* Right: Summary Card */}
          <aside className="cart-page__summary-card">
            <h2 className="cart-page__summary-title">Order Summary</h2>

            <div className="cart-page__summary-rows">
              <div className="cart-page__summary-row">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="cart-page__summary-row">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'cart-page__free' : ''}>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="cart-page__summary-row">
                <span>Tax (8%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
            </div>

            <div className="cart-page__summary-divider" />

            <div className="cart-page__summary-row cart-page__summary-row--total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>

            {shipping === 0 && (
              <div className="cart-page__shipping-note">
                <MdLocalShipping /> You qualify for free shipping!
              </div>
            )}

            <button className="cart-page__checkout-btn" onClick={() => navigate('/checkout')}>
              Proceed to Checkout <MdArrowForward />
            </button>

            <Link to="/products" className="cart-page__outline-btn">Continue Shopping</Link>

            <div className="cart-page__promo">
              <label className="cart-page__promo-label">Promo Code</label>
              <div className="cart-page__promo-row">
                <input type="text" className="cart-page__promo-input" placeholder="Enter code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} />
                <button className="cart-page__promo-btn" onClick={() => { if (promoCode.trim()) setPromoApplied(true); }}>Apply</button>
              </div>
              {promoApplied && <span className="cart-page__promo-msg">Promo code applied!</span>}
            </div>
          </aside>
        </div>
      </section>

      {/* ── Section 2: Carousel ── */}
      <section className="cart-page__carousel">
        <Carousel
          slides={[
            { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Explore premium selections and lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
            { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Stock up on trending drinks with exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
            { id: 3, image: image66, tag: 'TRENDING', title: 'New Packaging & Containers', subtitle: 'Premium retail containers — elevate your shelf appeal.', cta: { label: 'Browse Range' }, bgColor: '#1A2540' },
          ]}
          autoPlay
          interval={5000}
        />
      </section>

      {/* ── Section 3: Suggested Products ── */}
      <section className="cart-page__suggested">
        <div className="cart-page__suggested-inner">
          <h2 className="cart-page__section-title">You Might Also Like</h2>
          <div className="cart-page__suggested-grid">
            {suggested.map((p) => (
              <div key={p.id} className="cart-page__sug-card" onClick={() => navigate(`/products/${p.id}`)}>
                <div className="cart-page__sug-img-wrap">
                  <img src={p.image} alt={p.name} className="cart-page__sug-img" />
                </div>
                <div className="cart-page__sug-info">
                  <span className="cart-page__sug-name">{p.name}</span>
                  <span className="cart-page__sug-stock">In stock</span>
                  <div className="cart-page__sug-row">
                    <span className="cart-page__sug-price">${p.price.toFixed(2)}</span>
                    <AddToCartButton onClick={(e) => { e.stopPropagation(); handleAddSuggested(p); }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Popular Categories ── */}
      <PopularCategories />
    </div>
  );
};

export default CartPage;
